// src/containers/VehicleList/VehicleDisplay/VehicleTab/MpiFormBuilder/components/SimpleHeader.jsx
import React from 'react';
import { Box, Typography, Button, Stack, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { PictureAsPdf as PictureAsPdfIcon, GetApp as DownloadIcon, CheckCircle } from '@mui/icons-material';
import Logo from '../../../../../../assets/images/ipacket_logo.png';
import _ from 'lodash';

const placeholderThumbnail = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

const SimpleHeader = ({ 
  active_vehicle, 
  installation, 
  onCreatePdf,
  pdfUrl,
  isGeneratingPdf,
  proxiedThumbnailUrl = '',
  formDisplayName = 'Multi-Point Inspection Report',
  legendItems = [],
  onApproveAll,
  lastUpdated = null   // NEW: Last updated timestamp for tooltip
}) => {
  const thumbnailSrc = proxiedThumbnailUrl || 
    (_.find(active_vehicle?.vehicle_properties, { key: 'thumbnail' })?.value) || 
    placeholderThumbnail;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `MPI_${active_vehicle.stock || 'Report'}_${new Date().toISOString().slice(0,10)}.pdf`;
    link.click();
  };

  // Button text logic
  const buttonText = pdfUrl ? 'Update PDF' : 'Create PDF';

  return (
    <Box sx={{ mb: 3 }}>
      {/* TOP SECTION - Title + Dealer */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ borderBottom: '3px solid #1976d2', pb: 3, mb: 3 }}
      >
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#000' }}>
            {formDisplayName}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 500, color: '#000', mt: 1 }}>
            Powered by{' '}
            <img src={Logo} alt="iPacket logo" style={{ height: 60, verticalAlign: 'middle', marginLeft: 8 }} />
          </Typography>
        </Box>

        <Stack direction="row" spacing={3} alignItems="flex-start">
          {installation.logo && (
            <Box sx={{ flexShrink: 0 }}>
              <img
                src={installation.logo}
                alt="Dealer Logo"
                style={{ maxHeight: 90, maxWidth: 200, objectFit: 'contain' }}
              />
            </Box>
          )}
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {installation.softname || 'Dealership'}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
              {installation.phone || ''}<br />
              {installation.street1 || ''}<br />
              {installation.city && `${installation.city}, ${installation.state} ${installation.zip}`}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* VEHICLE INFO + PRINT + LEGEND + APPROVE ALL */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        {/* Left: Vehicle Photo + Info */}
        <Stack direction="row" spacing={3} alignItems="flex-start">
          <Box sx={{ flexShrink: 0 }}>
            <img
              src={thumbnailSrc}
              alt="Vehicle"
              style={{
                width: 160,
                height: 120,
                objectFit: 'cover',
                borderRadius: 8,
                border: '1px solid #ddd',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            />
          </Box>

          <Box>
            <Typography variant="h5" sx={{ color: '#333', fontWeight: 600 }}>
              {active_vehicle.year} {active_vehicle.make} {active_vehicle.model}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, color: '#555' }}>
              VIN: <strong>{active_vehicle.vin || 'N/A'}</strong>
            </Typography>
            <Typography variant="body1" sx={{ color: '#555' }}>
              Stock: <strong>{active_vehicle.stock || 'N/A'}</strong>
            </Typography>
            <Typography variant="body1" sx={{ color: '#555' }}>
              Miles: <strong>{active_vehicle.miles || 'N/A'}</strong>
            </Typography>
          </Box>
        </Stack>

        {/* Right: PDF Actions + Approve All + Legend */}
        <Stack direction="column" alignItems="flex-end" spacing={1} sx={{ minHeight: 120, justifyContent: 'space-between' }}>
          {/* PDF Actions with Tooltip */}
          <Stack direction="row" spacing={1}>
            {isGeneratingPdf ? (
              <CircularProgress size={24} color="primary" />
            ) : (
              <Tooltip 
                title={lastUpdated ? `Last updated: ${lastUpdated}` : 'Create new PDF report'}
                arrow
                placement="top"
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={onCreatePdf}
                  sx={{ py: 0.5, px: 1.5 }}
                >
                  {buttonText}
                </Button>
              </Tooltip>
            )}
            
            {pdfUrl && (
              <Tooltip title="Download PDF">
                <IconButton
                  color="primary"
                  size="small"
                  onClick={handleDownload}
                  aria-label="Download PDF"
                >
                  <DownloadIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {/* Global Pass All Button */}
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            onClick={onApproveAll}
            size="small"
          >
            Pass Entire Form
          </Button>

          {/* Legend */}
          {legendItems.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 3,
                flexWrap: 'wrap',
              }}
            >
              {legendItems.map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <i
                    className={`far fa-${item.icon}`}
                    style={{ fontSize: '26px', color: item.color }}
                    aria-hidden="true"
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default SimpleHeader;